import { AlertTriangle, CheckCircle2, ShieldAlert, ShieldCheck, Shield } from "lucide-react";
import { currencyExact } from "@/lib/api";

const RISK_STYLE = {
  HIGH: { cls: "bg-[#B54747] text-white", Icon: ShieldAlert, label: "High risk" },
  MEDIUM: { cls: "bg-[#D5A44A] text-black", Icon: Shield, label: "Medium risk" },
  LOW: { cls: "bg-emerald-600 text-white", Icon: ShieldCheck, label: "Low risk" },
  UNKNOWN: { cls: "bg-zinc-600 text-white", Icon: Shield, label: "Reviewed" },
};

function parseLines(review) {
  const out = [];
  for (const raw of (review || "").split("\n")) {
    const line = raw.trim().replace(/^[*\-•·\s]+/, "");
    if (!line) continue;
    const upper = line.toUpperCase();
    if (upper.startsWith("EXTRACTED_TOTAL:") || upper.startsWith("RISK:")) continue;
    if (upper.startsWith("FLAG:")) out.push({ type: "flag", text: line.slice(5).trim() });
    else if (upper.startsWith("OK:")) out.push({ type: "ok", text: line.slice(3).trim() });
    else if (upper.startsWith("SUMMARY:")) out.push({ type: "summary", text: line.slice(8).trim() });
    else out.push({ type: "text", text: line });
  }
  return out;
}

export function AuditReportView({ report }) {
  if (!report) return null;
  const risk = (report.risk || "UNKNOWN").toUpperCase();
  const rs = RISK_STYLE[risk] || RISK_STYLE.UNKNOWN;
  const lines = parseLines(report.review);
  const summary = lines.find((l) => l.type === "summary");
  const findings = lines.filter((l) => l.type === "flag" || l.type === "ok");

  return (
    <div data-testid="audit-report-view">
      {/* Stat header */}
      <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-6" data-testid="audit-stat-risk">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Independent verdict</p>
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono-data text-xs uppercase tracking-widest ${rs.cls}`}>
            <rs.Icon className="h-4 w-4" /> {rs.label}
          </span>
        </div>
        <div className="bg-white p-6" data-testid="audit-stat-total">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Invoice total read</p>
          <p className="font-display text-3xl">{report.extracted_total != null ? currencyExact(report.extracted_total) : "—"}</p>
        </div>
        <div className="bg-white p-6" data-testid="audit-stat-flags">
          <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Items flagged</p>
          <p className="font-display text-3xl text-[#B54747]">{report.flag_count ?? findings.filter((f) => f.type === "flag").length}</p>
        </div>
      </div>

      {summary && (
        <p className="mt-8 text-[15px] leading-relaxed text-zinc-800" data-testid="audit-summary">{summary.text}</p>
      )}

      {findings.length > 0 && (
        <div className="mt-6 space-y-2">
          {findings.map((f, i) => (
            <div
              key={i}
              data-testid={`audit-finding-${f.type}-${i}`}
              className={`flex items-start gap-3 border p-4 ${
                f.type === "flag" ? "border-[#B54747]/30 bg-[#B54747]/5" : "border-emerald-200 bg-emerald-50/50"
              }`}
            >
              {f.type === "flag" ? (
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#B54747]" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
              )}
              <p className="text-sm text-zinc-800 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      )}

      <details className="mt-8 border-t border-zinc-200 pt-4">
        <summary className="font-mono-data text-xs uppercase tracking-widest text-zinc-500 cursor-pointer" data-testid="audit-raw-toggle">
          Full auditor notes
        </summary>
        <div className="mt-4 prose prose-zinc max-w-none whitespace-pre-wrap leading-relaxed text-sm text-zinc-700">
          {report.review}
        </div>
      </details>
    </div>
  );
}
